import { FiInfo } from "react-icons/fi";

type BaseProps = {
	footer?: React.ReactNode;
	title?: string;
};

type WithListProps = BaseProps & {
	children?: never;
	inline?: false; // explicitly not inline
	instructions: React.ReactNode[];
	variant?: "bulleted" | "numbered";
};

type InlineProps = BaseProps & {
	children: React.ReactNode;
	inline: true;
	instructions?: never;
	variant?: never;
};

type InstructionsInfoBoxProps = WithListProps | InlineProps;

export const InstructionsInfoBox: React.FC<InstructionsInfoBoxProps> = ({
	title = "What you need to do",
	inline = false,
	instructions,
	variant = "bulleted",
	footer,
	children,
}) => {
	const isInline = inline === true;
	const hasInstructions = instructions && instructions.length > 0;

	const ListTag =
		hasInstructions && variant === "numbered" ? "ol" : "ul";

	return (
		<div className="rounded-lg border border-[#0031DD]/20 bg-[#f5f7ff] px-4 py-3">
			{/* INLINE MODE */}
			{isInline ? (
				<div className="flex items-center gap-2 text-sm text-gray-700">
					<FiInfo size={16} className="shrink-0 text-[#0031DD]" />
					<span>{children}</span>
				</div>
			) : (
				<>
					{/* HEADER */}
					<div className="flex items-start gap-2.5 mb-3">
						<FiInfo
							size={16}
							className="mt-0.5 shrink-0 text-[#0031DD]"
							aria-hidden="true"
						/>
						<p className="text-sm font-medium text-gray-900">{title}</p>
					</div>

					{/* LIST MODE */}
					{hasInstructions && (
						<ListTag
							className={`space-y-2 pl-6 text-sm text-gray-600 ${variant === "numbered"
								? "list-decimal"
								: "list-none"
								}`}
						>
							{instructions.map((item, index) => (
								<li key={index} className="flex items-start gap-2">
									{variant !== "numbered" && (
										<span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0031DD]" />
									)}
									<span>{item}</span>
								</li>
							))}
						</ListTag>
					)}

					{/* FALLBACK CONTENT */}
					{children && (
						<div className="text-sm text-gray-600">{children}</div>
					)}
				</>
			)}

			{/* FOOTER */}
			{footer && !isInline && (
				<div className="mt-3 border-t border-[#0031DD]/10 pt-3 text-xs text-gray-600">
					{footer}
				</div>
			)}
		</div>
	);
};